import { Injectable } from '@angular/core';
import { AppEnvironment } from 'environments/environment';
import { DeferredPromise } from '@cf-shared/shared-util';
import { FirebaseApp, initializeApp, deleteApp } from 'firebase/app';
import { Auth, initializeAuth, indexedDBLocalPersistence, browserLocalPersistence, browserPopupRedirectResolver } from 'firebase/auth';
import { _getInstance, browserPopupRedirectResolver as internalPopupRedirectResolver } from '@firebase/auth/internal';
import { isCapacitorNative } from 'environments/environment-helpers';
import {
  findWarmablePopupResolver, PopupResolverWarmupOutcome, PopupResolverWarmupResult, PopupResolverWarmupState, webAuthDependencies,
} from './popup-resolver-warmup';

@Injectable()
export class FirebaseService {
  public initialized = DeferredPromise.defer<void>();
  firebaseApp: FirebaseApp;
  fbAuth: Auth;

  // read by the login page and by the #6743 E2E scenario
  popupResolverState = PopupResolverWarmupState.NotStarted;
  private popupResolverWarmup: Promise<PopupResolverWarmupResult> | null = null;

  constructor() {
    this.firebaseApp = initializeApp(AppEnvironment.firebaseConfig);
    // on Capacitor native, use initializeAuth with explicit persistence to avoid
    // the gapi iframe which fails from capacitor://localhost origin
    this.fbAuth = isCapacitorNative()
      ? initializeAuth(this.firebaseApp, { persistence: [indexedDBLocalPersistence, browserLocalPersistence] })
      : initializeAuth(this.firebaseApp, webAuthDependencies());
    this.initialized.resolve(undefined);
  }

  /**
   * Web only: loads Google's sign-in frame now, so a later tap on a sign-in button opens the popup
   * straight away (#6743). Safe to call often: one load runs at a time, and a failed one is tried
   * again on the next call. The caller reports the outcome.
   */
  warmPopupResolverAsync(): Promise<PopupResolverWarmupResult> {
    if (isCapacitorNative()) return Promise.resolve({ outcome: PopupResolverWarmupOutcome.Ready });
    this.popupResolverWarmup ??= this.loadPopupResolverAsync();
    return this.popupResolverWarmup;
  }

  // one load of the sign-in frame, recording where it ended in popupResolverState
  private async loadPopupResolverAsync(): Promise<PopupResolverWarmupResult> {
    const resolver = findWarmablePopupResolver(browserPopupRedirectResolver, internalPopupRedirectResolver, _getInstance);
    if (!resolver) {
      this.popupResolverState = PopupResolverWarmupState.Unavailable;
      return { outcome: PopupResolverWarmupOutcome.Unavailable };
    }
    this.popupResolverState = PopupResolverWarmupState.Loading;
    try {
      await resolver._initialize(this.fbAuth);
      this.popupResolverState = PopupResolverWarmupState.Ready;
      return { outcome: PopupResolverWarmupOutcome.Ready };
    } catch (error) {
      // forget the failed load so the next call starts a new one; Firebase forgets it too
      this.popupResolverWarmup = null;
      this.popupResolverState = PopupResolverWarmupState.NotStarted;
      return { outcome: PopupResolverWarmupOutcome.Failed, error };
    }
  }

  // This is called when the app needs log out and clear local storage
  async shutdownAsync() {
    try {
    await deleteApp(this.firebaseApp);
    } catch (e) {
      if (typeof e.message === 'string' && e.message.includes('already deleted (app/app-deleted).')) {
        // we ignore this, otherwise it just nags the tests
        return;
      }
      throw e;
    }
  }
}
